// Text.tsx
import clsx from 'clsx';
import React from 'react';
import styles from './Text.module.scss';

type Props<T extends React.ElementType = 'span'> = {
  tag?: T;
  className?: string;
  children: string;
  isBold?: boolean;
  isFormattedText?: boolean;
} & Omit<React.ComponentPropsWithoutRef<T>, 'children' | 'className'>;

const tokenize = (str: string, isBold?: boolean): React.ReactNode[] => str
  .split(/(\*\*.+?\*\*|\[[^\]]+\]\([^)]+\))/g)
  .filter(Boolean)
  .map((chunk) => {
    // use kalnia font for isBold text unless isBold prop is true
    if (/^\*\*.+\*\*$/.test(chunk)) {
      return (
        <span
          key={chunk.slice(2, -2)}
          className={clsx(styles.tokenizeText, { isBold, 'font-kalnia': !isBold })}
        >
          {chunk.slice(2, -2)}
        </span>
      );
    }

    const linkMatch = chunk.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
    if (linkMatch) {
      return (
        <a
          key={linkMatch[2]}
          href={linkMatch[2]}
          target='_blank'
          rel='noopener noreferrer'
          className='underline'
        >
          {linkMatch[1]}
        </a>
      );
    }
    return chunk;
  });

function Text<T extends React.ElementType = 'span'>({
  tag,
  className,
  children,
  isBold,
  isFormattedText,
  ...rest
}: Props<T>) {
  if (!children) return null;
  const text = children.replace(/\\r\\n|\\n/g, '\n');
  const Component = tag || 'span';
  if (isFormattedText) {
    return (
      <Component
        {...rest}
        className={clsx(className)}
        dangerouslySetInnerHTML={{
          __html: children.replace(/\/n/g, '<br/>'),
        }}
      />
    );
  }
  return (
    <Component
      {...rest}
      className={clsx(styles.text, className)}
      style={{ ...(rest as any).style }}
    >
      {tokenize(text, isBold)}
    </Component>
  );
}

export default Text;
